import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { SettingsService } from '../../../core/settings/settings.service';
import { map, publishLast, refCount } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Settings } from '@app/core/_models/settings';
import { CommonModule } from '@angular/common';
import { LanguagePickerComponent } from '@app/shared/components/loyalty-club-header/language-picker/language-picker.component';

interface SettingsData {
  languages: string[];
  websiteUrl: string;
  logoUrl: string;
  name: string;
  additionalIndexHtml: string;
}

const mapSettings = (settings: Settings, useSubunitName: boolean): SettingsData => ({
  languages: settings.schema.languages,
  websiteUrl: settings.website_url,
  logoUrl: settings.logo_url,
  name: useSubunitName ? settings.getSubunitDisplayName() : settings.name,
  additionalIndexHtml: settings.additional_index_html,
});

@Component({
  selector: 'app-loyalty-club-header',
  templateUrl: './loyalty-club-header.component.html',
  styleUrl: 'loyalty-club-header.component.scss',
  imports: [
    CommonModule,
    LanguagePickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoyaltyClubHeaderComponent {
  public readonly settingsObservable: Observable<SettingsData>;

  @Input() public customHTML?: boolean | undefined;

  @Input()
  public set useSubunitName(userSubunitName: boolean) {
    this.useSubunitName$.next(userSubunitName);
  }

  private readonly useSubunitName$: BehaviorSubject<boolean>;

  constructor(private settingsService: SettingsService) {
    this.useSubunitName$ = new BehaviorSubject<boolean>(false);
    this.settingsObservable = this.mapSettings();
  }

  private mapSettings(): Observable<SettingsData> {
    return combineLatest([
      this.getSettings(),
      this.useSubunitName$,
    ]).pipe(
      map(([settings, useSubunitName]) => mapSettings(settings, useSubunitName)),
    );
  }

  private getSettings(): Observable<Settings> {
    return this.settingsService.getSettingsOnce()
      .pipe(
        publishLast(),
        refCount(),
      );
  }
}
